import React from "react";
import { TextileBlock, onTimeHint, type OrderKpis, type TextileSummary } from "./TextileBlock";
import { Link, useNavigate } from "react-router-dom";
import { Boxes, Briefcase, FileClock, Inbox, PackageCheck, Target, Truck } from "lucide-react";
import {
  BarList,
  DataTable,
  SectionCard,
  StatTile,
  TargetBar,
  fmtDate,
  fmtMoney,
  fmtNum,
  fmtPct,
  type Column,
} from "@/components/reports/ReportWidgets";
import type { ExecRow, ForecastBucket, StageRow } from "@/types/report";
import { DonutPieChart } from "@/components/charts/DonutPieChart";
import { RadialTargetGauge } from "@/components/charts/RadialTargetGauge";
import { FunnelConversionChart } from "@/components/charts/FunnelConversionChart";
import { GroupedBarChart } from "@/components/charts/GroupedBarChart";

export interface SalesHeadSummary {
  orders: OrderKpis;
  textile: TextileSummary;
  kpis: {
    revenue: number;
    revenue_prev: number;
    target_amount: number;
    target_achieved_pct: number | null;
    pipeline_amount: number;
    weighted_amount: number;
    win_rate_pct: number;
    win_rate_prev: number;
    conversion_pct: number;
    conversion_prev: number;
    pending_my_approval: number;
  };
  team_leaderboard: ExecRow[];
  pipeline_by_stage: StageRow[];
  forecast: { this_month: ForecastBucket; next_month: ForecastBucket; overdue: ForecastBucket };
  attention: {
    overdue_tasks: Array<{ id: string; title: string; due_at: string; related_type: string; related_id: string; related_name: string; owner_name: string }>;
    not_contacted_24h: Array<{ id: string; name: string; company_name: string; assigned_at: string; owner_name: string }>;
    stale_deals: Array<{ id: string; name: string; stage_name: string; owner_name: string; idle_days: number; amount: number }>;
    unassigned_leads: number;
  };
  approval_queue: Array<{
    id: string;
    display_number: string;
    quote_number: string;
    grand_total: number;
    max_discount_pct: number;
    submitted_at: string;
    owner_name: string;
    account_name: string;
  }>;
  activity_this_week: Array<{ user_id: string; name: string; calls: number; meetings: number; emails: number; total_activities: number; tasks_done: number }>;
}

function ListBlock<T>({ items, empty, render }: { items: T[]; empty: string; render: (item: T) => React.ReactNode }) {
  if (items.length === 0) return <p className="text-xs text-muted-foreground py-3">{empty}</p>;
  return <ul className="divide-y divide-border">{items.map(render)}</ul>;
}

export function SalesHeadDashboard({ data }: { data: SalesHeadSummary }) {
  const navigate = useNavigate();
  const k = data.kpis;
  const o = data.orders;

  const teamColumns: Column<ExecRow>[] = [
    { key: "name", header: "Executive", render: (r) => <span className="font-medium text-foreground">{r.name}</span> },
    { key: "leads_assigned", header: "Leads", align: "right" },
    { key: "conversion_pct", header: "Conversion", align: "right", render: (r) => fmtPct(r.conversion_pct) },
    { key: "deals_won", header: "Won", align: "right" },
    { key: "won_amount", header: "Revenue", align: "right", render: (r) => fmtMoney(r.won_amount) },
    { key: "tasks_overdue", header: "Overdue tasks", align: "right" },
    {
      key: "target_achieved_pct",
      header: "Target",
      sortValue: (r) => r.target_achieved_pct ?? -1,
      render: (r) => <TargetBar achieved={r.won_amount} target={r.target_amount} compact />,
    },
  ];

  const taskLink = (t: { related_type: string; related_id: string }) =>
    t.related_type === "DEAL" && t.related_id ? `/deals/${t.related_id}` : "/tasks";

  return (
    <div className="space-y-5">
      <TextileBlock data={data.textile} scope="Across your team" />

      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
        <StatTile
          label="Team orders booked (this month)"
          value={fmtMoney(o.order_value)}
          rawValue={o.order_value}
          previous={o.order_value_prev}
          icon={<PackageCheck className="size-4" />}
          hint={<span>{fmtNum(o.orders_booked)} orders · {fmtMoney(o.dispatched_value)} dispatched</span>}
        />
        <StatTile
          label="Team target"
          value={k.target_achieved_pct === null ? "—" : fmtPct(k.target_achieved_pct)}
          icon={<Target className="size-4" />}
          hint={k.target_amount ? <TargetBar achieved={k.revenue} target={k.target_amount} /> : <span>Not set by Super Admin</span>}
        />
        <StatTile
          label="Quotes waiting for me"
          value={fmtNum(k.pending_my_approval)}
          icon={<FileClock className="size-4" />}
          hint={<Link to="/quotes" className="text-primary hover:underline">Review</Link>}
        />
        <StatTile
          label="Team open enquiries"
          value={fmtMoney(k.pipeline_amount)}
          icon={<Briefcase className="size-4" />}
          hint={<span>{fmtMoney(k.weighted_amount)} weighted · {fmtPct(k.conversion_pct)} lead conversion</span>}
        />
        <StatTile
          label="Team dispatch on time"
          value={o.on_time_pct === null ? "—" : fmtPct(o.on_time_pct)}
          icon={<Truck className="size-4" />}
          hint={<span>{onTimeHint(o)}</span>}
        />
        <StatTile
          label="Order book to dispatch"
          value={fmtMoney(o.order_book_value)}
          icon={<Boxes className="size-4" />}
          hint={<Link to="/orders" className="text-primary hover:underline">{fmtNum(o.order_book_orders)} open orders</Link>}
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <SectionCard title="Team target pacing" description="Won value against the team's monthly target">
          <RadialTargetGauge
            achieved={k.revenue}
            target={k.target_amount}
            label="Team Quota"
            formatMoney={(v) => fmtMoney(v)}
            size={180}
          />
        </SectionCard>

        <SectionCard
          title="Pipeline value by stage"
          description="Distribution of active deals"
          action={<Link to="/deals" className="text-xs text-primary hover:underline">All deals</Link>}
        >
          <DonutPieChart
            data={data.pipeline_by_stage.map((s) => ({
              label: s.stage_name,
              value: s.amount,
              sublabel: `${s.deals} deals`,
            }))}
            formatValue={(v) => fmtMoney(v)}
            centerValue={fmtMoney(k.pipeline_amount)}
            centerLabel="Pipeline"
            donut={true}
            onSliceClick={() => navigate("/deals")}
          />
        </SectionCard>

        <SectionCard
          title="Pipeline conversion funnel"
          description="Stage drop-off and conversion rates"
          action={<Link to="/reports?tab=pipeline" className="text-xs text-primary hover:underline">Details</Link>}
        >
          <FunnelConversionChart
            stages={data.pipeline_by_stage.map((s) => ({
              name: s.stage_name,
              count: s.deals,
              amount: s.amount,
            }))}
            formatMoney={(v) => fmtMoney(v)}
            onStageClick={() => navigate("/deals")}
          />
        </SectionCard>
      </div>

      <SectionCard
        title="Team leaderboard"
        description="This month, click a name for details"
        action={
          <span className="flex gap-3 whitespace-nowrap">
            <Link to="/targets" className="text-xs text-primary hover:underline">Set targets</Link>
            <Link to="/reports" className="text-xs text-primary hover:underline">Full report</Link>
          </span>
        }
      >
        <DataTable
          columns={teamColumns}
          rows={data.team_leaderboard}
          rowKey={(r) => r.user_id}
          onRowClick={(r) => navigate(`/reports/user/${r.user_id}`)}
          emptyMessage="No Sales Executives report to you yet."
        />
      </SectionCard>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <SectionCard
          title="Team activity this week"
          description="Calls, meetings, emails and tasks logged per executive"
          className="lg:col-span-2"
        >
          <GroupedBarChart
            data={data.activity_this_week.map((a) => ({
              category: a.name,
              calls: a.calls,
              meetings: a.meetings,
              emails: a.emails,
              tasks: a.tasks_done,
            }))}
            series={[
              { key: "calls", name: "Calls", color: "#3b82f6" },
              { key: "meetings", name: "Meetings", color: "#8b5cf6" },
              { key: "emails", name: "Emails", color: "#06b6d4" },
              { key: "tasks", name: "Tasks Done", color: "#10b981" },
            ]}
            height={210}
          />
        </SectionCard>

        <SectionCard title="Forecast" description="Open deals by expected close date">
          <dl className="space-y-3 text-xs">
            {[
              { label: "This month", b: data.forecast.this_month },
              { label: "Next month", b: data.forecast.next_month },
              { label: "Past expected date", b: data.forecast.overdue },
            ].map(({ label, b }) => (
              <div key={label} className="flex items-baseline justify-between gap-2 border-b border-border/60 pb-2 last:border-0">
                <dt className="text-muted-foreground">
                  {label} <span className="tabular-nums">({b.deals} deals)</span>
                </dt>
                <dd className="text-right">
                  <span className="block text-sm font-bold tabular-nums text-foreground">{fmtMoney(b.weighted)}</span>
                  <span className="block text-[11px] text-muted-foreground tabular-nums">of {fmtMoney(b.amount)}</span>
                </dd>
              </div>
            ))}
          </dl>
        </SectionCard>
      </div>

      <SectionCard title="Approval queue" description="Quotes waiting for your decision, oldest first">
        <ListBlock
          items={data.approval_queue}
          empty="Nothing waiting for approval."
          render={(q) => (
            <li key={q.id}>
              <Link to={`/quotes/${q.id}`} className="flex items-center gap-3 py-2 hover:bg-accent/50 rounded-md px-1">
                <span className="flex-1 min-w-0">
                  <span className="block text-xs font-medium text-foreground truncate">
                    {q.display_number || q.quote_number} · {q.account_name || "—"}
                  </span>
                  <span className="block text-[11px] text-muted-foreground truncate">
                    {q.owner_name} · submitted {fmtDate(q.submitted_at)}
                  </span>
                </span>
                <span className="text-right">
                  <span className="block text-xs font-semibold tabular-nums text-foreground">{fmtMoney(q.grand_total)}</span>
                  <span className="block text-[11px] text-muted-foreground tabular-nums">{fmtPct(q.max_discount_pct)} discount</span>
                </span>
              </Link>
            </li>
          )}
        />
      </SectionCard>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <SectionCard title="Overdue tasks" description="Across your team">
          <ListBlock
            items={data.attention.overdue_tasks}
            empty="No overdue tasks."
            render={(t) => (
              <li key={t.id}>
                <Link to={taskLink(t)} className="block py-2 hover:bg-accent/50 rounded-md px-1">
                  <span className="block text-xs font-medium text-foreground truncate">{t.title}</span>
                  <span className="block text-[11px] text-muted-foreground truncate">
                    {t.owner_name} · due {fmtDate(t.due_at)}
                  </span>
                </Link>
              </li>
            )}
          />
        </SectionCard>
        <SectionCard title="Not contacted in 24h" description="New leads nobody has called yet">
          <ListBlock
            items={data.attention.not_contacted_24h}
            empty="Every new lead has been contacted."
            render={(l) => (
              <li key={l.id} className="py-2 px-1">
                <span className="block text-xs font-medium text-foreground truncate">{l.name || l.company_name}</span>
                <span className="block text-[11px] text-muted-foreground truncate">
                  {l.owner_name} · assigned {fmtDate(l.assigned_at)}
                </span>
              </li>
            )}
          />
        </SectionCard>
        <SectionCard
          title="Stale deals"
          description="No activity for 14+ days"
          action={
            data.attention.unassigned_leads > 0 ? (
              <Link to="/deals" className="inline-flex items-center gap-1 text-xs text-primary hover:underline">
                <Inbox className="size-3.5" aria-hidden /> {data.attention.unassigned_leads} in pool
              </Link>
            ) : undefined
          }
        >
          <ListBlock
            items={data.attention.stale_deals}
            empty="No stale deals."
            render={(d) => (
              <li key={d.id}>
                <Link to={`/deals/${d.id}`} className="flex items-center gap-2 py-2 hover:bg-accent/50 rounded-md px-1">
                  <span className="flex-1 min-w-0">
                    <span className="block text-xs font-medium text-foreground truncate">{d.name}</span>
                    <span className="block text-[11px] text-muted-foreground truncate">
                      {d.owner_name} · {d.stage_name}
                    </span>
                  </span>
                  <span className="text-[11px] text-muted-foreground tabular-nums">{d.idle_days}d idle</span>
                </Link>
              </li>
            )}
          />
        </SectionCard>
      </div>
    </div>
  );
}
